import { apiClient } from './apiClient';
import { API_ENDPOINTS } from '../config/apiConfig';
import {
  Member,
  MemberInput,
  ActivityRecord,
  ActivityInput,
  ActivityCategory,
  ActivityCategoryVersion,
  User,
  TokenTransfer,
  TokenTransferInput,
  OrganizationSummary,
  MemberTokenSummary,
  SharePointSummary,
  MembershipPayment,
  MembershipPaymentInput,
  MembershipStatus,
  TrackerSummary,
  TrackerActivityStats,
  ActivityRecordSummary,
} from '../types/api';

// Dashboard API
export const dashboardApi = {
  getOrganizationSummary: (): Promise<OrganizationSummary> =>
    apiClient.get(API_ENDPOINTS.organizationSummary),
  
  getMemberTokens: (): Promise<MemberTokenSummary[]> =>
    apiClient.get(API_ENDPOINTS.memberTokens),
  
  getSharePointSummary: (): Promise<SharePointSummary> =>
    apiClient.get(API_ENDPOINTS.sharePointSummary),
};

// Members API
export const membersApi = {
  getAll: (): Promise<Member[]> =>
    apiClient.get(API_ENDPOINTS.members),
  
  getById: (id: number): Promise<Member> =>
    apiClient.get(API_ENDPOINTS.memberById(id)),
  
  create: (member: MemberInput): Promise<Member> =>
    apiClient.post(API_ENDPOINTS.members, member),
  
  update: (id: number, member: MemberInput): Promise<Member> =>
    apiClient.put(API_ENDPOINTS.memberById(id), member),
  
  delete: (id: number): Promise<void> =>
    apiClient.delete(API_ENDPOINTS.memberById(id)),
  
  getDeleteInfo: (id: number): Promise<{
    member_name: string;
    is_active: boolean;
    activity_count: number;
    cycle_count: number;
    transfer_count: number;
    has_related_data: boolean;
    can_hard_delete: boolean;
  }> =>
    apiClient.get(`${API_ENDPOINTS.memberById(id)}/delete-info`),
  
  softDelete: (id: number): Promise<void> =>
    apiClient.put(`${API_ENDPOINTS.memberById(id)}/soft-delete`, {}),
};

// Activities API
export const activitiesApi = {
  create: (activity: ActivityInput): Promise<ActivityRecord> =>
    apiClient.post(API_ENDPOINTS.activities, activity),
  
  getAll: (): Promise<ActivityRecord[]> =>
    apiClient.get(API_ENDPOINTS.activities),
  
  getMemberActivities: (memberId: number): Promise<ActivityRecord[]> =>
    apiClient.get(API_ENDPOINTS.memberActivities(memberId)),
};

// Categories API
export const categoriesApi = {
  getAll: (): Promise<ActivityCategory[]> =>
    apiClient.get(API_ENDPOINTS.categories),
  
  create: (category: { label: string }): Promise<ActivityCategory> =>
    apiClient.post(API_ENDPOINTS.categories, category),
  
  getVersionsByYear: (year: number): Promise<ActivityCategoryVersion[]> =>
    apiClient.get(API_ENDPOINTS.categoryVersionsByYear(year)),
  
  createVersion: (version: Omit<ActivityCategoryVersion, 'version_id'>): Promise<ActivityCategoryVersion> =>
    apiClient.post(API_ENDPOINTS.categoryVersions, version),
  
  updateVersion: (versionId: number, updates: Partial<ActivityCategoryVersion>): Promise<ActivityCategoryVersion> =>
    apiClient.put(`${API_ENDPOINTS.categoryVersions}/${versionId}`, updates),
};

// Users API
export const usersApi = {
  getAll: (): Promise<User[]> =>
    apiClient.get(API_ENDPOINTS.users),
  
  getById: (id: number): Promise<User> =>
    apiClient.get(API_ENDPOINTS.userById(id)),
};

// Token Ledgers API
export const tokenLedgersApi = {
  getAll: (): Promise<TokenTransfer[]> =>
    apiClient.get(API_ENDPOINTS.tokenLedgers),
  
  create: (transfer: TokenTransferInput): Promise<TokenTransfer> =>
    apiClient.post(API_ENDPOINTS.tokenLedgers, transfer),
  
  getMemberTransfers: (memberId: number): Promise<TokenTransfer[]> =>
    apiClient.get(API_ENDPOINTS.memberTransfers(memberId)),
};

// Membership Fees API
export const membershipFeesApi = {
  getMemberStatus: (memberId: number, cycleYear: number = 2025): Promise<MembershipStatus> =>
    apiClient.get(`${API_ENDPOINTS.memberFeeStatus(memberId)}?cycle_year=${cycleYear}`),
  
  recordPayment: (payment: MembershipPaymentInput): Promise<MembershipPayment> =>
    apiClient.post(API_ENDPOINTS.membershipPayments, payment),
  
  getOrganizationFeeSummary: (cycleYear: number = 2025): Promise<any> =>
    apiClient.get(`${API_ENDPOINTS.organizationFeeSummary}?cycle_year=${cycleYear}`),
  
  getMemberActivitiesByTracker: (memberId: number, cycleYear: number = 2025): Promise<any> =>
    apiClient.get(`${API_ENDPOINTS.memberActivitiesByTracker(memberId)}?cycle_year=${cycleYear}`),
  
  getTrackerActivities: (trackerName: string, cycleYear: number = 2025): Promise<any[]> =>
    apiClient.get(`${API_ENDPOINTS.trackerActivitiesByMember(trackerName)}?cycle_year=${cycleYear}`),
  
  updateMustDoStatus: (memberId: number, cycleYear: number = 2025): Promise<any> =>
    apiClient.put(`${API_ENDPOINTS.updateMustDoStatus(memberId)}?cycle_year=${cycleYear}`, {}),
};

// Tracker Dashboard API
export const trackerDashboardApi = {
  getTrackersSummary: (cycleYear: number = 2025): Promise<TrackerSummary[]> =>
    apiClient.get(`${API_ENDPOINTS.trackersSummary}?cycle_year=${cycleYear}`),
  
  getTrackerRecords: (trackerName: string, cycleYear: number = 2025, limit: number = 50, offset: number = 0): Promise<ActivityRecordSummary[]> =>
    apiClient.get(`${API_ENDPOINTS.trackerRecords(trackerName)}?cycle_year=${cycleYear}&limit=${limit}&offset=${offset}`),
  
  getTrackerActivityStats: (trackerName: string, cycleYear: number = 2025): Promise<TrackerActivityStats[]> =>
    apiClient.get(`${API_ENDPOINTS.trackerActivityStats(trackerName)}?cycle_year=${cycleYear}`),
  
  getMemberTrackerView: (memberId: number, cycleYear: number = 2025): Promise<any> =>
    apiClient.get(`${API_ENDPOINTS.memberTrackerView(memberId)}?cycle_year=${cycleYear}`),
  
  getCompulsoryActivitiesStatus: (cycleYear: number = 2025): Promise<any[]> =>
    apiClient.get(`${API_ENDPOINTS.compulsoryActivitiesStatus}?cycle_year=${cycleYear}`),
};

// Health API
export const healthApi = {
  check: (): Promise<{ status: string; service: string }> =>
    apiClient.get(API_ENDPOINTS.health),
};